import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, ChevronDown } from 'lucide-react';

export function Products() {
  const [selectedCategory, setSelectedCategory] = React.useState('all');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [showFilters, setShowFilters] = React.useState(false);

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'privacy', name: 'Privacy Management' },
    { id: 'consent', name: 'Consent Management' },
    { id: 'assessment', name: 'Privacy Assessment' },
    { id: 'dsar', name: 'DSAR Management' },
    { id: 'vendor', name: 'Vendor Risk Management' }
  ];

  const products = [
    {
      id: 'cookie-consent-manager',
      name: 'Cookie Consent Manager',
      category: 'consent',
      description: 'Comprehensive cookie consent and preference management solution',
      image: 'https://images.unsplash.com/photo-1633419461186-7d40a38105ec?auto=format&fit=crop&q=80&w=400',
      price: 'Starting at $499/mo'
    },
    {
      id: 'privacy-rights-portal',
      name: 'Privacy Rights Portal',
      category: 'privacy',
      description: 'Streamline privacy rights requests and compliance',
      image: 'https://images.unsplash.com/photo-1560732488-6b0df240254a?auto=format&fit=crop&q=80&w=400',
      price: 'Starting at $399/mo'
    },
    {
      id: 'assessment-automation',
      name: 'Assessment Automation',
      category: 'assessment',
      description: 'Automate privacy impact assessments and data mapping',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=400',
      price: 'Starting at $599/mo'
    },
    {
      id: 'dsar-automation-suite',
      name: 'DSAR Automation Suite',
      category: 'dsar',
      description: 'Automated data subject access request management',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=400',
      price: 'Starting at $299/mo'
    },
    {
      id: 'vendor-risk-scanner',
      name: 'Vendor Risk Scanner',
      category: 'vendor',
      description: 'Comprehensive vendor risk assessment and monitoring',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=400',
      price: 'Starting at $449/mo'
    },
    {
      id: 'consent-analytics-dashboard',
      name: 'Consent Analytics Dashboard',
      category: 'consent',
      description: 'Advanced analytics for consent management',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=400',
      price: 'Starting at $199/mo'
    }
  ];

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-all duration-300">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#5e9bb8] to-[#499da8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="text-4xl font-bold text-white mb-4">OneTrust Products</h1>
          <p className="text-xl text-white/90">
            Comprehensive privacy and compliance solutions for your business
          </p>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 shadow-lg z-30 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-5 w-5" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl border-gray-300 dark:border-gray-600 focus:ring-[#5e9bb8] focus:border-[#5e9bb8] dark:bg-gray-700/50 dark:text-white transition-all duration-300"
              />
            </div>
            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300"
              >
                <Filter className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                <span className="text-gray-700 dark:text-gray-300">Filter</span>
                <ChevronDown className="h-4 w-4 text-gray-700 dark:text-gray-300" />
              </button>
              {showFilters && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg z-40 overflow-hidden">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setSelectedCategory(category.id);
                        setShowFilters(false);
                      }}
                      className={`block w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 ${
                        selectedCategory === category.id ? 'bg-[#5e9bb8]/10 dark:bg-[#5e9bb8]/20 text-[#5e9bb8] dark:text-[#5e9bb8]' : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map(product => (
            <div 
              key={product.id} 
              className="group bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
            >
              <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-48 object-cover transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white group-hover:text-[#5e9bb8] dark:group-hover:text-[#5e9bb8] transition-colors duration-300 mb-2">
                  {product.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">{product.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-[#5e9bb8] dark:text-[#5e9bb8] font-medium">{product.price}</span>
                  <Link
                    to={`/products/${product.id}`}
                    className="px-4 py-2 bg-gradient-to-r from-[#5e9bb8] to-[#499da8] hover:from-[#4d8299] hover:to-[#3d8289] text-white rounded-xl font-medium transform hover:-translate-y-0.5 transition-all duration-300"
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-lg">No products found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Products;